import { IssueState, IssueType } from "@boark/shared/types/issue";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  IssueForm,
  IssueFormRecord,
  IssueFormRecordType,
  IssueFormValues,
  issueFormValuesToCreate,
} from "../../components/issue-form";
import { Modal } from "../../components/modal";
import { useGetAssociation } from "../../queries/get-association";
import { useGetAssociationContext } from "../../queries/get-association-context";
import { createIssue } from "../../services/issue";
import { getId } from "../../utils/id";
import { navigateBack } from "../../utils/navigate-back";

const ISSUE_FORM_RECORD_TYPES: readonly string[] = [
  "apartment",
  "holding",
  "lease",
  "sublet",
  "person",
  "organization",
] satisfies readonly IssueFormRecordType[];

function isIssueFormRecordType(
  value: string | null,
): value is IssueFormRecordType {
  return value !== null && ISSUE_FORM_RECORD_TYPES.includes(value);
}

function defaultRecordsFromSearchParams(
  searchParams: URLSearchParams,
): IssueFormRecord[] {
  const type = searchParams.get("recordType");
  const recordId = searchParams.get("recordId");

  if (!isIssueFormRecordType(type) || !recordId) {
    return [];
  }

  return [
    {
      type,
      recordId,
      label: searchParams.get("recordLabel") ?? undefined,
    },
  ];
}

export function IssueCreate({
  defaultType = IssueType.ISSUE,
  defaultParentIdentifier,
  fallbackRoute = "/admin/issue/list",
  typeDisabled = false,
  parentDisabled = false,
}: {
  defaultType?: IssueType;
  defaultParentIdentifier?: number;
  fallbackRoute?: string;
  typeDisabled?: boolean;
  parentDisabled?: boolean;
} = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { data: associationContext } = useGetAssociationContext();
  const { data: association } = useGetAssociation(
    getId(associationContext?.id ?? ""),
  );
  const hasFastumIntegration = Boolean(association?.integrations?.fastum);

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<IssueFormValues>({
    title: "",
    description: "",
    tags: [],
    state: IssueState.TODO,
    type: defaultType,
    parentIdentifier: defaultParentIdentifier,
    links: [],
    records: defaultRecordsFromSearchParams(searchParams),
    fastumTickets: [],
  });

  const closeModal = () => {
    navigateBack(navigate, fallbackRoute);
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <IssueForm
        hasError={hasError}
        loading={loading}
        parentDisabled={parentDisabled}
        typeDisabled={typeDisabled}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await createIssue(
                issueFormValuesToCreate(formValues, {
                  includeFastumTickets:
                    hasFastumIntegration && formValues.fastumTickets.length > 0,
                }),
              );
              await queryClient.invalidateQueries({ queryKey: ["issues"] });
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
